import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { apiRequest } from "@/helper/api";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import type { RootState } from "@/components/state/store";
import type { BulkActionsSlotContext } from "@/components/tables/toolbar/bulkActionsSlot";

type NotificationVariant = "success" | "error" | "warning" | "info";
type SelectedUser = Record<string, any>;

export interface UseUserBulkActionsOptions {
  companySubjectId?: string;
  refreshTableAfterAction?: () => void;
  notify: (message: string, variant: NotificationVariant) => void;
  onError: (error: any) => void;
}

export function useUserBulkActions(options: UseUserBulkActionsOptions) {
  const { t } = useTranslation();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const selectedChildCompany: any = useSelector(
    (state: RootState) => state.parent
  );

  const impersonationParameters = () =>
    options.companySubjectId
      ? { impersonate_subject: options.companySubjectId }
      : {};

  const joinNames = (users: SelectedUser[]) =>
    users.map((user) => user.first_name + " " + user.last_name).join(", ");

  const deletePeople = async (
    selectedUsers: SelectedUser[],
    table: BulkActionsSlotContext
  ) => {
    const numberOfSelectedUsers = selectedUsers.length;
    const confirmationMessage =
      numberOfSelectedUsers < 10
        ? `${t("bulk_delete_entries_question", {
            numberOfSelectedUsers,
          })} (${joinNames(selectedUsers)})`
        : t("bulk_delete_many_entries_question", { numberOfSelectedUsers });
    const isConfirmed = await showConfirmationModalAsync(confirmationMessage);
    if (!isConfirmed) {
      return;
    }

    try {
      await Promise.all(
        selectedUsers.map((user) =>
          apiRequest({
            method: "delete",
            entity: "user",
            entityId: user.id,
            parametersToRender: {
              additionalUrlParameters: impersonationParameters(),
              depth: "0",
            },
          }).then((res) => {
            options.refreshTableAfterAction?.(); // preserved quirk: runs once per deleted user
            return res;
          })
        )
      );
      table.clearSelection();
      options.notify(t("delete_successful"), "success");
      table.requestReload();
    } catch (error) {
      options.onError(error);
    }
  };

  const undeletePeople = async (
    selectedUsers: SelectedUser[],
    table: BulkActionsSlotContext
  ) => {
    const numberOfSelectedUsers = selectedUsers.length;
    const confirmationMessage =
      numberOfSelectedUsers < 10
        ? `${t("bulk_undelete_entries_question", {
            numberOfSelectedUsers,
          })} (${joinNames(selectedUsers)})`
        : t("bulk_undelete_many_entries_question", { numberOfSelectedUsers });
    const isConfirmed = await showConfirmationModalAsync(confirmationMessage);
    if (!isConfirmed) {
      return;
    }

    try {
      await Promise.all(
        selectedUsers.map((user) =>
          apiRequest({
            entity: "user",
            entityId: user.id,
            method: "put",
            submitData: { is_active: true, email: user.email },
          })
        )
      );
      options.notify(t("user_successfully_undeleted"), "success");
      table.requestReload();
      table.clearSelection();
    } catch (error) {
      options.onError(error);
      table.requestReload();
      table.clearSelection();
    }
  };

  const sendRegistrationMails = async (
    selectedUsers: SelectedUser[],
    table: BulkActionsSlotContext
  ) => {
    const numberOfSelectedUsers = selectedUsers.length;
    const confirmationMessage =
      numberOfSelectedUsers < 10
        ? `${t(
            "bulk_send_registration_or_passwordreset_email_to_selected_users",
            { numberOfSelectedUsers }
          )} (${selectedUsers.map((user) => user.email).join(", ")})`
        : t("bulk_send_registration_or_passwordreset_email_to_many_users", {
            numberOfSelectedUsers,
          });
    const isConfirmed = await showConfirmationModalAsync(confirmationMessage);
    if (!isConfirmed) {
      return;
    }

    try {
      await Promise.all(
        selectedUsers.map((user) =>
          apiRequest({
            entity: "subject",
            method: "post",
            entityId: user.subject_id,
            additionalRouteParts: { send_user_registration_email: "/" },
            submitData: {
              impersonate_subject: selectedChildCompany.subjectId,
            },
          })
        )
      );
      options.notify(t("successfully_sent"), "success");
      table.requestReload();
      table.clearSelection();
    } catch (error) {
      options.onError(error);
    }
  };

  return { deletePeople, undeletePeople, sendRegistrationMails };
}
